import { useApplications } from "../hooks/queries/useApplications";
import { useAuth } from "../context/AuthContext";
import { LogOut, ChevronRight, User, Briefcase } from "lucide-react";
import type { ApplicationStatus } from "@applicant-reviewer/api";

interface MyApplicationsProps {
  onViewDetail?: (id: string) => void;
}

const STATUS_LABELS: Record<ApplicationStatus, string> = {
  draft: "Draft",
  submitted: "Submitted",
  reviewing: "Under Review",
  interview: "Interview Scheduled",
  offered: "Offer Extended",
  hired: "Hired",
  benched: "On Bench",
  rejected: "Not Selected",
};

const STATUS_COLORS: Record<ApplicationStatus, string> = {
  draft: "bg-gray-100 text-gray-500",
  submitted: "bg-blue-100 text-blue-700",
  reviewing: "bg-blue-100 text-blue-700",
  interview: "bg-purple-100 text-purple-700",
  offered: "bg-green-100 text-green-700",
  hired: "bg-emerald-100 text-emerald-700",
  benched: "bg-yellow-100 text-yellow-700",
  rejected: "bg-gray-100 text-gray-500",
};

export default function MyApplications({ onViewDetail }: MyApplicationsProps) {
  const { logout } = useAuth();
  const { data: applications, isLoading, isError } = useApplications();

  const list = applications ?? [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900" style={{ fontFamily: "Nunito, sans-serif" }}>
              My Applications
            </h1>
            {!isLoading && !isError && (
              <p className="text-sm text-gray-500 mt-1">
                {list.length} application{list.length !== 1 ? "s" : ""}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { window.location.href = "/jobs"; }}
              className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <Briefcase className="w-4 h-4" />
              Browse Jobs
            </button>
            <button
              onClick={() => { window.location.href = "/profile"; }}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <User className="w-4 h-4" />
              My Profile
            </button>
            <button
              onClick={() => { logout(); window.location.href = "/login/candidate"; }}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Log out
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <div className="w-10 h-10 border-4 border-[#0156FF] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-base text-gray-500">Loading your applications...</p>
          </div>
        </div>
      ) : isError ? (
        <div className="flex items-center justify-center py-24 px-6">
          <div className="text-center">
            <p className="text-xl font-bold text-gray-900 mb-2">Unable to Load Applications</p>
            <p className="text-base text-gray-500">Please try again later.</p>
          </div>
        </div>
      ) : (
      <div className="px-4 py-6 space-y-3 max-w-2xl mx-auto">
        {list.length === 0 ? (
          <div className="text-center py-16">
            <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-lg font-semibold text-gray-700 mb-2">No applications yet</p>
            <p className="text-sm text-gray-500 mb-6">
              When you apply for jobs, they will appear here.
            </p>
            <button
              type="button"
              onClick={() => { window.location.href = "/jobs"; }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors"
            >
              <Briefcase className="w-4 h-4" />
              Browse Open Jobs
            </button>
          </div>
        ) : (
          list.map((app) => (
            <button
              key={app.applicationId}
              type="button"
              onClick={() => onViewDetail?.(app.applicationId)}
              className="w-full text-left bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:border-gray-200 hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h2 className="text-base font-bold text-gray-900 truncate">Application</h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Applied {new Date(app.submittedAt ?? app.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span
                    className={`text-xs font-semibold px-3 py-1 rounded-full ${
                      STATUS_COLORS[app.status]
                    }`}
                  >
                    {STATUS_LABELS[app.status]}
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
              </div>
            </button>
          ))
        )}
      </div>
      )}
    </div>
  );
}